import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation, NavLink } from "react-router-dom";
import { Compass, HeartHandshake, MessageSquare, User, Settings } from "lucide-react";
import { Logo, Spinner } from "@/components/kit";
import { api } from "@/lib/api";

const TABS = [
  { to: "/app/discover", label: "Discover", icon: Compass, testid: "nav-discover-tab" },
  { to: "/app/requests", label: "Requests", icon: HeartHandshake, testid: "nav-requests-tab" },
  { to: "/app/chats", label: "Chats", icon: MessageSquare, testid: "nav-chats-tab" },
  { to: "/app/profile", label: "Profile", icon: User, testid: "nav-profile-tab" },
];

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    api.get("/profile/completion").then(({ data }) => {
      if (!data.published) { navigate("/onboarding", { replace: true }); return; }
      setChecked(true);
    }).catch(() => setChecked(true));
  }, [navigate]);

  if (!checked) {
    return (
      <div className="min-h-[100dvh] bg-[#F8F6F1] flex items-center justify-center"><Spinner /></div>
    );
  }

  const onSettings = location.pathname.endsWith("/settings");
  const showMobileNav = !onSettings;

  return (
    <div className="min-h-[100dvh] bg-[#F8F6F1] lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-72 shrink-0 border-r border-[#E3DCD0] bg-[#F8F6F1] sticky top-0 h-[100dvh] px-5 py-7">
        <div className="px-2">
          <Logo variant="horizontal" className="h-9" />
        </div>
        <nav className="mt-10 space-y-1.5" data-testid="sidebar-nav">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} data-testid={`${t.testid}-desktop`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 font-sans text-[15px] transition-colors ${isActive ? "bg-[#253B35] text-[#F8F6F1]" : "text-[#5F6D66] hover:bg-[#F1EEE7]"}`}>
              <t.icon className="h-5 w-5" strokeWidth={1.7} />
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto">
          <NavLink to="/app/settings" data-testid="nav-settings-desktop"
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-4 py-3 font-sans text-[15px] transition-colors ${isActive ? "bg-[#253B35] text-[#F8F6F1]" : "text-[#5F6D66] hover:bg-[#F1EEE7]"}`}>
            <Settings className="h-5 w-5" strokeWidth={1.7} />
            Settings
          </NavLink>
          <p className="px-4 pt-4 font-sans text-[11px] text-[#B2BAB4]">Sincerr · Private beta · v1.0</p>
        </div>
      </aside>

      {/* Content */}
      <main className={`flex-1 min-w-0 ${showMobileNav ? "pb-24 lg:pb-10" : ""}`}>
        <div className="mx-auto w-full max-w-2xl">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      {showMobileNav && (
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#F8F6F1]/95 backdrop-blur-md border-t border-[#E3DCD0] px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] flex justify-around items-center z-50" data-testid="bottom-nav">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} data-testid={t.testid} className="flex flex-col items-center gap-1 py-1.5 px-3 min-w-[64px]">
              {({ isActive }) => (
                <>
                  <t.icon className={`h-[22px] w-[22px] ${isActive ? "text-[#253B35]" : "text-[#8A9790]"}`} strokeWidth={isActive ? 2 : 1.6} />
                  <span className={`font-sans text-[11px] ${isActive ? "text-[#253B35] font-medium" : "text-[#8A9790]"}`}>{t.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
